/**
 * The contact roles on a quote (SA-450, wireframe decision 40).
 *
 * A record names a contractual partner and a cargo agent beside its customer.
 * On nearly every enquiry both roles ARE the customer, so the agenda stores a
 * company name only for the exception: an empty role is read as "same as
 * customer". There is no stored boolean and no third state — absence is the
 * whole rule.
 *
 * This lives in core rather than in the Compass module because the built-in
 * RegisterView is one of its three consumers. It is deliberately shared
 * (unlike RegisterView's private `slot()`, which PricingRegisterView repeats):
 * "Same as customer" is user-visible wording with a three-state rule, and it
 * must read identically on both register views and on the record.
 */
import { ClampedText } from './ClampedText'

/** The agenda slot behind each role. */
export const ROLE_SLOTS = { partner: 'contractual_partner', agent: 'cargo_agent' } as const

export type RoleReading =
  /** The enquiry named a different firm. */
  | { kind: 'named'; name: string }
  /** Empty, and a customer is known: the role is the customer itself. */
  | { kind: 'same' }
  /** Empty, and no customer either: nothing to say. */
  | { kind: 'none' }

const words = (value: string | null | undefined): string => (typeof value === 'string' ? value.trim() : '')

export function roleReading(
  role: string | null | undefined,
  customer: string | null | undefined,
): RoleReading {
  const named = words(role)
  if (named) return { kind: 'named', name: named }
  return words(customer) ? { kind: 'same' } : { kind: 'none' }
}

/** `clamp` is for a register cell (SA-447: free text in a table clamps to two
 * lines with the full text on hover); the record shows the name in full. */
export function RoleValue({
  role, customer, clamp = false,
}: {
  role: string | null
  customer: string | null
  clamp?: boolean
}) {
  const reading = roleReading(role, customer)
  if (reading.kind === 'none') return null
  if (reading.kind === 'same') return <span className="text-muted-foreground">Same as customer</span>
  return clamp ? <ClampedText text={reading.name} /> : <span>{reading.name}</span>
}
